import { createContext, useContext } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import type { QuizLanguage } from "./index";
import { shouldShowLanguageSwitch } from "./language";

type QuizLanguageContextValue = {
  language: QuizLanguage;
  setLanguage: (lang: QuizLanguage) => void;
};

export const QuizLanguageContext = createContext<QuizLanguageContextValue | null>(null);

export function useQuizLanguage(): QuizLanguage {
  return useContext(QuizLanguageContext)?.language ?? "en";
}

const Switch = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  line-height: 1;
  color: #7a7a7a;
`;

const Option = styled.button<{ $active: boolean }>`
  border: none;
  background: none;
  padding: 4px 3px;
  font: inherit;
  cursor: ${(p) => (p.$active ? "default" : "pointer")};
  color: ${(p) => (p.$active ? "#0B6DFF" : "#7a7a7a")};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  text-decoration: ${(p) => (p.$active ? "none" : "underline")};
`;

/**
 * English / Español. Rendered only for visitors who see Spanish or who chose a
 * language themselves, so English traffic (and the web47 control arm) sees
 * exactly the pages it saw before.
 */
export function LanguageSwitch({ className }: { className?: string }) {
  const ctx = useContext(QuizLanguageContext);
  const { t } = useTranslation();
  if (!ctx || !shouldShowLanguageSwitch(ctx.language)) return null;
  const options: QuizLanguage[] = ["en", "es"];
  return (
    <Switch className={className} role="group" aria-label={t("common.languageSwitch.label")} data-testid="language-switch">
      {options.map((lang, i) => (
        <span key={lang}>
          {i > 0 && <span aria-hidden="true"> / </span>}
          <Option
            type="button"
            lang={lang}
            $active={ctx.language === lang}
            aria-pressed={ctx.language === lang}
            onClick={() => ctx.language !== lang && ctx.setLanguage(lang)}
            data-lang={lang}
          >
            {t(`common.languageSwitch.${lang}`)}
          </Option>
        </span>
      ))}
    </Switch>
  );
}
